import {
	FileText,
	MousePointer2,
	Palette,
	Shield,
	Type,
	Variable,
} from "lucide-react";
import PropTypes from "prop-types";

export default function TemplateDesignEditor({
	theme,
	variables,
	onChange,
	acceptanceConfig,
	onAcceptanceChange,
}) {
	const handleThemeChange = (field, value) => {
		onChange({ ...variables }, { ...theme, [field]: value });
	};

	const handleVarChange = (key, value) => {
		onChange({ ...variables, [key]: value }, { ...theme });
	};

	const handleAcceptanceToggle = (field) => {
		onAcceptanceChange({
			...acceptanceConfig,
			[field]: !acceptanceConfig[field],
		});
	};

	const handleAcceptanceText = (val) => {
		onAcceptanceChange({ ...acceptanceConfig, legal_text: val });
	};

	return (
		<div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
			{/* Visual Design Section */}
			<div className="bg-slate-900/40 rounded-xl border border-white/5 p-4">
				<div className="flex items-center gap-2 mb-4 border-b border-white/5 pb-2">
					<Palette className="w-4 h-4 text-indigo-400" />
					<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">
						Aparência Visual
					</h3>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<label
							htmlFor="primaryColor"
							className="text-[10px] uppercase font-bold text-slate-500 tracking-tight"
						>
							Cor Primária
						</label>
						<div className="flex gap-2">
							<input
								id="primaryColor"
								type="color"
								value={theme.primaryColor}
								onChange={(e) =>
									handleThemeChange("primaryColor", e.target.value)
								}
								className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer"
							/>
							<input
								type="text"
								aria-label="Código da Cor Primária"
								value={theme.primaryColor}
								onChange={(e) =>
									handleThemeChange("primaryColor", e.target.value)
								}
								className="flex-1 bg-slate-950 border border-white/10 rounded px-2 py-1 text-[11px] font-mono text-slate-300"
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor="secondaryColor"
							className="text-[10px] uppercase font-bold text-slate-500 tracking-tight"
						>
							Cor Secundária
						</label>
						<div className="flex gap-2">
							<input
								id="secondaryColor"
								type="color"
								value={theme.secondaryColor}
								onChange={(e) =>
									handleThemeChange("secondaryColor", e.target.value)
								}
								className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer"
							/>
							<input
								type="text"
								aria-label="Código da Cor Secundária"
								value={theme.secondaryColor}
								onChange={(e) =>
									handleThemeChange("secondaryColor", e.target.value)
								}
								className="flex-1 bg-slate-950 border border-white/10 rounded px-2 py-1 text-[11px] font-mono text-slate-300"
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor="fontFamily"
							className="text-[10px] uppercase font-bold text-slate-500 tracking-tight flex items-center gap-1"
						>
							<Type className="w-3 h-3" /> Fonte Principal
						</label>
						<select
							id="fontFamily"
							value={theme.fontFamily}
							onChange={(e) => handleThemeChange("fontFamily", e.target.value)}
							className="w-full bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-[11px] text-slate-300 outline-none focus:border-indigo-500/50"
						>
							<option value="Inter">Inter (Padrão)</option>
							<option value="Poppins">Poppins (Web)</option>
							<option value="Roboto">Roboto</option>
							<option value="Montserrat">Montserrat</option>
							<option value="Outfit">Outfit (Premium)</option>
						</select>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor="borderRadius"
							className="text-[10px] uppercase font-bold text-slate-500 tracking-tight flex items-center gap-1"
						>
							<MousePointer2 className="w-3 h-3" /> Arredondamento
						</label>
						<div className="flex items-center gap-2">
							<input
								id="borderRadius"
								type="range"
								min="0"
								max="32"
								step="4"
								value={theme.borderRadius || 12}
								onChange={(e) =>
									handleThemeChange("borderRadius", e.target.value)
								}
								className="flex-1 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-500"
							/>
							<span className="text-[10px] font-mono text-slate-400 w-6">
								{theme.borderRadius}px
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Acceptance Settings */}
			{acceptanceConfig && (
				<div className="bg-slate-900/40 rounded-xl border border-white/5 p-4">
					<div className="flex items-center gap-2 mb-4 border-b border-white/5 pb-2">
						<Shield className="w-4 h-4 text-emerald-400" />
						<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">
							Aceite Digital
						</h3>
					</div>

					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<span className="text-xs text-slate-400">
								Mostrar Seção de Aceite
							</span>
							<button
								type="button"
								aria-label={
									acceptanceConfig.show_acceptance
										? "Desativar seção de aceite"
										: "Ativar seção de aceite"
								}
								onClick={() => handleAcceptanceToggle("show_acceptance")}
								className={`w-10 h-5 rounded-full relative transition-colors ${acceptanceConfig.show_acceptance ? "bg-emerald-500/40" : "bg-slate-800"}`}
							>
								<div
									className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${acceptanceConfig.show_acceptance ? "left-6" : "left-1"}`}
								></div>
							</button>
						</div>

						<div className="grid grid-cols-2 gap-3">
							<button
								type="button"
								onClick={() => handleAcceptanceToggle("require_role")}
								className={`flex items-center gap-2 px-3 py-2 rounded border text-[10px] transition-all ${acceptanceConfig.require_role ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-300" : "bg-slate-950/40 border-white/5 text-slate-500"}`}
							>
								Exigir Cargo
							</button>
							<button
								type="button"
								onClick={() => handleAcceptanceToggle("require_phone")}
								className={`flex items-center gap-2 px-3 py-2 rounded border text-[10px] transition-all ${acceptanceConfig.require_phone ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-300" : "bg-slate-950/40 border-white/5 text-slate-500"}`}
							>
								Exigir Telefone
							</button>
						</div>

						<div className="space-y-1.5 pt-1">
							<label
								htmlFor="legal_text"
								className="text-[10px] uppercase font-bold text-slate-500 tracking-tight flex items-center gap-1"
							>
								<FileText className="w-3 h-3" /> Texto Legal
							</label>
							<textarea
								id="legal_text"
								value={acceptanceConfig.legal_text}
								onChange={(e) => handleAcceptanceText(e.target.value)}
								className="w-full bg-slate-950/50 border border-white/5 rounded px-3 py-2 text-xs text-slate-400 focus:text-white min-h-[60px] resize-none focus:border-indigo-500/30 outline-none"
							/>
						</div>
					</div>
				</div>
			)}

			{/* Variables Section */}
			<div className="bg-slate-900/40 rounded-xl border border-white/5 p-4">
				<div className="flex items-center gap-2 mb-4 border-b border-white/5 pb-2">
					<Variable className="w-4 h-4 text-cyan-400" />
					<h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">
						Variáveis de Preview
					</h3>
					<span className="ml-auto text-[9px] text-slate-500">
						Use {"{{key}}"} no código
					</span>
				</div>

				<div className="space-y-3">
					{Object.entries(variables).map(([key, val]) => (
						<div key={key} className="flex gap-3 items-center group">
							<div className="w-24 shrink-0 overflow-hidden text-ellipsis whitespace-nowrap">
								<span className="text-[10px] font-mono text-cyan-500 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20">
									{key}
								</span>
							</div>
							<input
								type="text"
								value={val}
								onChange={(e) => handleVarChange(key, e.target.value)}
								className="flex-1 bg-slate-950/50 border border-white/5 rounded px-3 py-1.5 text-xs text-slate-400 focus:text-white transition-colors outline-none focus:border-cyan-500/30"
							/>
						</div>
					))}
					<p className="text-[9px] text-slate-600 mt-2 italic px-1">
						* Estas variáveis são usadas apenas como exemplo no editor. Na
						proposta real, serão substituídas pelos dados reais do cliente.
					</p>
				</div>
			</div>
		</div>
	);
}

TemplateDesignEditor.propTypes = {
	theme: PropTypes.shape({
		primaryColor: PropTypes.string,
		secondaryColor: PropTypes.string,
		fontFamily: PropTypes.string,
		borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}).isRequired,
	variables: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	acceptanceConfig: PropTypes.shape({
		show_acceptance: PropTypes.bool,
		require_role: PropTypes.bool,
		require_phone: PropTypes.bool,
		legal_text: PropTypes.string,
	}),
	onAcceptanceChange: PropTypes.func,
};
